import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { ToasterProvider } from "./context/ToasterContext";
import { GoogleOAuthProvider } from "@react-oauth/google";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import MeMeetPage from "./pages/MeMeetPage";
import InvitationActionPage from "./pages/InvitationActionPage";
import LiveKitTestPage from "./pages/LiveKitTestPage";
import "./App.css";

function resolveReturnPathFromState(state) {
  const returnTo = String(state?.returnTo || "").trim();
  if (returnTo.startsWith("/") && returnTo !== "/login" && returnTo !== "/register") {
    return returnTo;
  }

  const from = String(state?.from || "").trim();
  if (!from.startsWith("/") || from === "/login" || from === "/register") {
    return "";
  }

  const fromSearch = typeof state?.fromSearch === "string" ? state.fromSearch : "";
  const fromHash = typeof state?.fromHash === "string" ? state.fromHash : "";
  return `${from}${fromSearch}${fromHash}`;
}

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const { roomId } = useParams();

  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="app-loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    const returnTo = `${location.pathname}${location.search || ""}${location.hash || ""}`;
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname, fromSearch: location.search, fromHash: location.hash, returnTo, roomId }}
        replace
      />
    );
  }

  return children;
}

// Public route that redirects authenticated users
function PublicRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="app-loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (isAuthenticated) {
    const redirectTo = resolveReturnPathFromState(location.state);
    return <Navigate to={redirectTo || "/"} replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/login/:meMeetRoomId"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />

      <Route path="/accept-invitation/*" element={<InvitationActionPage mode="accept" />} />
      <Route path="/reject-invitation/*" element={<InvitationActionPage mode="reject" />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MeMeetPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/memeet/:roomId"
        element={
          <ProtectedRoute>
            <MeMeetPage />
          </ProtectedRoute>
        }
      />

      {/* LiveKit SFU test sayfası — eski WebRTC akışını bozmaz */}
      <Route
        path="/livekit-test"
        element={
          <ProtectedRoute>
            <LiveKitTestPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/livekit-test/:roomId"
        element={
          <ProtectedRoute>
            <LiveKitTestPage />
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Google Sign-In istemci kimligi .env'den gelir (REACT_APP_GOOGLE_CLIENT_ID).
// Bos ise saglayici yine kurulur; yalnizca Google dugmesi calismaz.
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <Router>
      <LanguageProvider>
        <ToasterProvider>
          <AuthProvider>
            <div className="app">
              <AppRoutes />
            </div>
          </AuthProvider>
        </ToasterProvider>
      </LanguageProvider>
    </Router>
    </GoogleOAuthProvider>
  );
}

export default App;
